import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store.types';
import { getLocationHash, removeHashFromHistory } from 'core-framework/window/history';

export enum OverlayType {
    TextSearch = 'TextSearch',
    AvailabilityCalendar = 'AvailabilityCalendar',
    SortAndFilters = 'SortAndFilters',
    CartSummary = 'CartSummary',
    CartInterstitial = 'CartInterstitial',
    RiskFreeCancellation = 'RiskFreeCancellation',
    Review = 'Review',
    GeoLocationDenied = 'GeoLocationDenied',
    NoActivitiesNearby = 'NoActivitiesNearby',
    CategoriesBottomSheet = 'CategoriesBottomSheet',
    InfoOverlay = 'InfoOverlay',
}

export interface OverlayState {
    visibleOverlay: Partial<Record<OverlayType, boolean>>;
}

const initialState: OverlayState = {
    visibleOverlay: {},
};

export const getOverlaysFromUrlHash = (urlHash = getLocationHash()) => {
    if (!urlHash) return [];
    const trimHash = urlHash.trim();
    if (!trimHash || trimHash === '#') return [];

    const overlays = trimHash
        .split('#')
        .filter((h) => h)
        .map((h) => OverlayType[h.trim() as keyof typeof OverlayType])
        .filter((o) => o) // Remove undefined values
        .filter((o, index, self) => self.indexOf(o) === index); // Remove duplicates
    return overlays;
};

export const getOverlaysHashes = (...overlayTypes: OverlayType[]) => {
    const targetOverlayTypes = overlayTypes.filter((overlayType) => overlayType).filter((o, index, self) => self.indexOf(o) === index);
    if (!targetOverlayTypes || targetOverlayTypes.length === 0) return '';
    return `#${targetOverlayTypes.join('#')}`;
};

const getVisibleOverlays = (visibleOverlays: Partial<Record<OverlayType, boolean>>): OverlayType[] => {
    return Object.entries(visibleOverlays)
        .map(([key, isVisible]) => (isVisible ? key : undefined))
        .filter((value) => value) as OverlayType[];
};

const overlay = createSlice({
    name: 'overlay',
    initialState,
    reducers: {
        showOverlay: (state, action: PayloadAction<OverlayType>) => {
            state.visibleOverlay[action.payload] = true;
        },
        closeOverlay: (state, action: PayloadAction<OverlayType>) => {
            state.visibleOverlay[action.payload] = false;
        },
        closeAllOverlays: (state) => {
            Object.entries(OverlayType).forEach(([_key, overlayType]) => {
                if (state.visibleOverlay[overlayType]) {
                    state.visibleOverlay[overlayType] = false;
                }
            });
        },
        closeTopOverlayAndHandleHistory: (state, action: PayloadAction<{ overlay?: OverlayType; shouldGoBack: boolean }>) => {
            const visibleOverlay = action.payload && action.payload.overlay ? [action.payload.overlay] : getVisibleOverlays(state.visibleOverlay);

            if (visibleOverlay.length > 0) {
                const topOverlay = visibleOverlay[visibleOverlay.length - 1];
                state.visibleOverlay[topOverlay] = false;
                removeHashFromHistory(topOverlay, action.payload.shouldGoBack);
            }
        },
        setOverlayStateFromUrl: (state, action: PayloadAction<string>) => {
            const overlays = getOverlaysFromUrlHash(action.payload);
            if (overlays.length > 0) {
                state.visibleOverlay[overlays[0]] = true;
            }
        },
    },
});

export const selectOverlayState = (state: RootState) => state.overlay;

const isOverlayVisible = (state: OverlayState, overlayType: OverlayType) => {
    return state.visibleOverlay && state.visibleOverlay[overlayType] === true;
};

export const isAnyOverlayVisible = (state: OverlayState) => {
    if (!state.visibleOverlay) return true;
    const anyOverlayVisible = Object.entries(state.visibleOverlay).reduce((acc, [_overlayTpe, isVisible]) => {
        return acc || isVisible;
    }, false);
    return anyOverlayVisible;
};

export const selectIsAnyOverlayVisible = createSelector(selectOverlayState, isAnyOverlayVisible);
export const selectTextSearchOverlayVisible = createSelector(selectOverlayState, (state) => isOverlayVisible(state, OverlayType.TextSearch));
export const selectAvailabilityCalendarOverlayVisible = createSelector(selectOverlayState, (state) => isOverlayVisible(state, OverlayType.AvailabilityCalendar));
export const selectSortAndFiltersOverlayVisible = createSelector(selectOverlayState, (state) => isOverlayVisible(state, OverlayType.SortAndFilters));
export const selectCartSummaryOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.CartSummary));
export const selectCartInterstitialOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.CartInterstitial));
export const selectRiskFreeCancellationOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.RiskFreeCancellation));
export const selectReviewOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.Review));
export const selectGeoLocationDeniedOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.GeoLocationDenied));
export const selectNoActivitiesNearbyOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.NoActivitiesNearby));
export const selectCategoriesBottomSheetVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.CategoriesBottomSheet));
export const selectInfoOverlayVisible = createSelector(selectOverlayState, (state: OverlayState) => isOverlayVisible(state, OverlayType.InfoOverlay));

export const selectVisibleOverlays = createSelector(selectOverlayState, (state): OverlayType[] => getVisibleOverlays(state.visibleOverlay));

export const { setOverlayStateFromUrl, showOverlay, closeOverlay, closeAllOverlays, closeTopOverlayAndHandleHistory } = overlay.actions;
export default overlay.reducer;
